import React from 'react';
import { 
  Typography, 
  Box, 
  Card, 
  CardContent, 
  Grid, 
  Avatar, 
  Chip,
  LinearProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  faGraduationCap, 
  faBook, 
  faTrophy, 
  faIdCard,
  faUser,
  faCalendar,
  faStar,
  faChartLine
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';

const Membro: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const stats = [
    { label: 'Lições Concluídas', value: 12, total: 20, icon: faGraduationCap, color: '#4CAF50' },
    { label: 'Declarações', value: 8, total: 15, icon: faBook, color: '#2196F3' },
    { label: 'Pontos', value: 850, total: 1000, icon: faTrophy, color: '#FF9800' },
    { label: 'Ranking', value: 5, total: 100, icon: faChartLine, color: '#9C27B0' }
  ];

  const quickActions = [
    { label: 'Continuar Lição', icon: faGraduationCap, path: '/licao', color: '#4CAF50' },
    { label: 'Nova Declaração', icon: faBook, path: '/declaracao', color: '#2196F3' },
    { label: 'Ver Ranking', icon: faTrophy, path: '/rank', color: '#FF9800' },
    { label: 'Minha Carteirinha', icon: faIdCard, path: '/carteirinha', color: '#9C27B0' }
  ];

  return (
    <Box className="membro-page fade-in" sx={{ p: 3, minHeight: '100vh' }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ color: '#225477', fontWeight: 'bold' }}>
          Bem-vindo de volta, {user?.nome || 'Membro'}!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Continue sua jornada de aprendizado e crescimento espiritual
        </Typography>
      </Box>

      {/* User Profile Card */}
      <Card sx={{ mb: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Avatar 
              src={user?.perfil || '/assets/images/perfil.png'} 
              alt="Perfil"
              sx={{ width: 80, height: 80, border: '3px solid #225477' }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" gutterBottom sx={{ color: '#225477' }}>
                {user?.nome || 'Nome do Usuário'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Chip 
                  icon={<FontAwesomeIcon icon={faUser} />} 
                  label="Membro Ativo" 
                  color="primary" 
                  variant="outlined"
                />
                <Chip 
                  icon={<FontAwesomeIcon icon={faCalendar} />} 
                  label="Desde 2024" 
                  variant="outlined"
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FontAwesomeIcon icon={faStar} style={{ color: '#FFD700' }} />
                <Typography variant="body2" color="text.secondary">
                  Nível 3 - Estudante Dedicado
                </Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card className="stats-card" sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', height: '100%' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ 
                    width: 50, 
                    height: 50, 
                    borderRadius: 2, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    backgroundColor: `${stat.color}20`
                  }}>
                    <FontAwesomeIcon icon={stat.icon} style={{ color: stat.color, fontSize: '1.5rem' }} />
                  </Box>
                  <Typography variant="h4" sx={{ color: stat.color, fontWeight: 'bold' }}>
                    {stat.value}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {stat.label}
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={(stat.value / stat.total) * 100} 
                  sx={{ 
                    height: 8, 
                    borderRadius: 4,
                    backgroundColor: `${stat.color}20`,
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: stat.color
                    }
                  }} 
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {stat.value} de {stat.total}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Quick Actions */}
      <Typography variant="h5" gutterBottom sx={{ color: '#225477', mb: 3 }}>
        Ações Rápidas
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {quickActions.map((action, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card 
              className="quick-action-card"
              sx={{ 
                borderRadius: 3, 
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)', 
                cursor: 'pointer'
              }}
              onClick={() => navigate(action.path)}
            >
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <Box sx={{ 
                  width: 60, 
                  height: 60, 
                  borderRadius: '50%', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  backgroundColor: `${action.color}20`,
                  mx: 'auto',
                  mb: 2
                }}>
                  <FontAwesomeIcon icon={action.icon} style={{ color: action.color, fontSize: '1.8rem' }} />
                </Box>
                <Typography variant="h6" sx={{ color: '#225477', fontWeight: '600' }}>
                  {action.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Recent Activity */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ color: '#225477', mb: 3 }}>
            Atividade Recente
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {[
              { action: 'Completou a lição "Os Sete Mandamentos"', time: '2 horas atrás', icon: faGraduationCap, color: '#4CAF50' },
              { action: 'Submeteu uma nova declaração', time: '1 dia atrás', icon: faBook, color: '#2196F3' },
              { action: 'Ganhou 50 pontos no ranking', time: '3 dias atrás', icon: faTrophy, color: '#FF9800' },
              { action: 'Atualizou informações do perfil', time: '1 semana atrás', icon: faUser, color: '#9C27B0' }
            ].map((activity, index) => (
                             <Box key={index} className="activity-item" sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, borderRadius: 2, backgroundColor: '#f8f9fa' }}>
                <Box sx={{ 
                  width: 40, 
                  height: 40, 
                  borderRadius: '50%', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  backgroundColor: `${activity.color}20`
                }}>
                  <FontAwesomeIcon icon={activity.icon} style={{ color: activity.color }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: '500' }}>
                    {activity.action}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {activity.time}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Membro;
